// frontend/dashboard-landing/Navbar.jsx
import React, { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import logo from '../images/logo.png';
import { useTranslation } from '../contexts/TranslationContext';

const Navbar = () => {
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { lang, t, changeLanguage } = useTranslation();

  const activeLinkStyle = { color: '#2563EB', fontWeight: 600 }; // Blue accent for active links

  return (
    <header className="bg-white shadow-md sticky top-0 z-50">
      {/* Top Bar - Hidden on mobile */}
      <div className="hidden md:block" style={{ backgroundColor: '#1B3A5C' }}>
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-12">
            <div className="flex items-center space-x-6">
              <Link to="/compliances" className="text-sm text-gray-300 hover:text-white transition-colors duration-200">
                {t('navbar.compliances')}
              </Link>
              <Link to="/privacy-policy" className="text-sm text-gray-300 hover:text-white transition-colors duration-200">
                {t('navbar.dataProtection')}
              </Link>
              <Link to="/contact" className="text-sm text-gray-300 hover:text-white transition-colors duration-200">
                {t('navbar.contactUs')}
              </Link>
              <Link to="/booking" className="text-sm text-gray-300 hover:text-white transition-colors duration-200">
                {t('navbar.bookAMeeting')}
              </Link>
            </div>

            <div className="flex items-center space-x-4">
              <select
                aria-label="Language selection"
                className="bg-white/15 border border-white/25 rounded-md text-sm text-white py-1 pl-2 pr-8 focus:outline-none focus:ring-1 focus:ring-white/40 hover:bg-white/25 transition cursor-pointer"
                value={lang}
                onChange={(e) => changeLanguage(e.target.value)}
              >
                <option value="en" className="text-gray-900 bg-white">English</option>
                <option value="es" className="text-gray-900 bg-white">Español</option>
              </select>
              <a
                href="/login"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-full text-sm font-semibold transition-colors duration-200 shadow-md"
              >
                {t('navbar.logIn')}
              </a>
            </div>
          </div>
        </div>
      </div>

      <nav className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center h-16 gap-6">
          <div className="flex-shrink-0">
            <Link to="/" className="block">
              <div className="rounded-xl py-1.5 px-1.5" style={{
                background: 'linear-gradient(to right, #11253b 0%, #2a5a9e 100%)',
                boxShadow: '0 2px 10px rgba(17, 37, 59, 0.2)',
                border: '1px solid rgba(255, 255, 255, 0.15)'
              }}>
                <img
                  className="h-10 w-auto"
                  style={{
                    imageRendering: 'auto',
                    filter: 'brightness(1.1) contrast(1.05)'
                  }}
                  src={logo}
                  alt="Investment Platform Logo"
                />
              </div>
            </Link>
          </div>

          <div className="hidden md:flex md:items-center md:space-x-4 lg:space-x-5 xl:space-x-7 flex-wrap md:flex-nowrap">
            <NavLink
              to="/"
              style={({ isActive }) => (isActive ? activeLinkStyle : undefined)}
              className="text-sm lg:text-base text-gray-600 hover:text-blue-600 transition-colors duration-200 font-medium whitespace-nowrap"
            >
              {t('navbar.home')}
            </NavLink>
            <NavLink
              to="/market-reports"
              style={({ isActive }) => (isActive ? activeLinkStyle : undefined)}
              className="text-sm lg:text-base text-gray-600 hover:text-blue-600 transition-colors duration-200 font-medium whitespace-nowrap"
            >
              {t('navbar.marketReports')}
            </NavLink>
            <NavLink
              to="/managed-portfolio-service"
              style={({ isActive }) => (isActive ? activeLinkStyle : undefined)}
              className="text-sm lg:text-base text-gray-600 hover:text-blue-600 transition-colors duration-200 font-medium whitespace-nowrap"
            >
              {t('navbar.modelPortfolioService')}
            </NavLink>
            <NavLink
              to="/literature"
              style={({ isActive }) => (isActive ? activeLinkStyle : undefined)}
              className="text-sm lg:text-base text-gray-600 hover:text-blue-600 transition-colors duration-200 font-medium whitespace-nowrap"
            >
              {t('navbar.literature')}
            </NavLink>
            <NavLink
              to="/investor-resources"
              style={({ isActive }) => (isActive ? activeLinkStyle : undefined)}
              className="text-sm lg:text-base text-gray-600 hover:text-blue-600 transition-colors duration-200 font-medium whitespace-nowrap"
            >
              {t('navbar.investorResources')}
            </NavLink>
            <NavLink
              to="/labuan-foundation"
              style={({ isActive }) => (isActive ? activeLinkStyle : undefined)}
              className="text-sm lg:text-base text-gray-600 hover:text-blue-600 transition-colors duration-200 font-medium whitespace-nowrap"
            >
              {t('navbar.labuanFoundation')}
            </NavLink>
            <NavLink
              to="/ifa-corporate-finance"
              style={({ isActive }) => (isActive ? activeLinkStyle : undefined)}
              className="text-sm lg:text-base text-gray-600 hover:text-blue-600 transition-colors duration-200 font-medium whitespace-nowrap"
            >
              {t('navbar.ifaCorporateFinance')}
            </NavLink>
          </div>

          <div className="md:hidden flex items-center ml-auto">
            <button
              onClick={() => setMobileMenuOpen(!isMobileMenuOpen)}
              aria-controls="mobile-menu"
              aria-expanded={isMobileMenuOpen}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
            >
              <span className="sr-only">Open main menu</span>
              {isMobileMenuOpen ? <XMarkIcon className="h-8 w-8" /> : <Bars3Icon className="h-8 w-8" />}
            </button>
          </div>
        </div>
      </nav>

      {isMobileMenuOpen && (
        <div id="mobile-menu" className="md:hidden fixed inset-x-0 top-[5rem] bottom-0 bg-white border-t border-gray-200 overflow-y-auto z-40">
          <div className="px-2 pt-2 pb-6 space-y-1 sm:px-3">
            {/* Main Navigation */}
            <NavLink
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              style={({ isActive }) => (isActive ? activeLinkStyle : undefined)}
              className="block px-3 py-3 rounded-md text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50"
            >
              {t('navbar.home')}
            </NavLink>
            <NavLink
              to="/market-reports"
              onClick={() => setMobileMenuOpen(false)}
              style={({ isActive }) => (isActive ? activeLinkStyle : undefined)}
              className="block px-3 py-3 rounded-md text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50"
            >
              {t('navbar.marketReports')}
            </NavLink>
            <NavLink
              to="/managed-portfolio-service"
              onClick={() => setMobileMenuOpen(false)}
              style={({ isActive }) => (isActive ? activeLinkStyle : undefined)}
              className="block px-3 py-3 rounded-md text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50"
            >
              {t('navbar.modelPortfolioService')}
            </NavLink>
            <NavLink
              to="/literature"
              onClick={() => setMobileMenuOpen(false)}
              style={({ isActive }) => (isActive ? activeLinkStyle : undefined)}
              className="block px-3 py-3 rounded-md text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50"
            >
              {t('navbar.literature')}
            </NavLink>
            <NavLink
              to="/investor-resources"
              onClick={() => setMobileMenuOpen(false)}
              style={({ isActive }) => (isActive ? activeLinkStyle : undefined)}
              className="block px-3 py-3 rounded-md text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50"
            >
              {t('navbar.investorResources')}
            </NavLink>
            <NavLink
              to="/labuan-foundation"
              onClick={() => setMobileMenuOpen(false)}
              style={({ isActive }) => (isActive ? activeLinkStyle : undefined)}
              className="block px-3 py-3 rounded-md text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50"
            >
              {t('navbar.labuanFoundation')}
            </NavLink>
            <NavLink
              to="/ifa-corporate-finance"
              onClick={() => setMobileMenuOpen(false)}
              style={({ isActive }) => (isActive ? activeLinkStyle : undefined)}
              className="block px-3 py-3 rounded-md text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50"
            >
              {t('navbar.ifaCorporateFinance')}
            </NavLink>

            {/* Divider */}
            <div className="border-t border-gray-200 my-2"></div>

            {/* Top Bar Items for Mobile */}
            <NavLink 
              to="/compliances" 
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-3 rounded-md text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50"
            >
              {t('navbar.compliances')}
            </NavLink>
            <NavLink 
              to="/privacy-policy" 
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-3 rounded-md text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50"
            >
              {t('navbar.dataProtection')}
            </NavLink>
            <NavLink 
              to="/contact" 
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-3 rounded-md text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50"
            >
              {t('navbar.contactUs')}
            </NavLink>
            <NavLink 
              to="/booking" 
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-3 rounded-md text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50"
            >
              {t('navbar.bookAMeeting')}
            </NavLink>

            {/* Language Selector and Login for Mobile */}
            <div className="px-3 py-3 space-y-3">
              <select
                aria-label="Language selection"
                className="w-full bg-gray-100 border-gray-300 rounded-md text-sm text-gray-900 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                value={lang}
                onChange={(e) => changeLanguage(e.target.value)}
              >
                <option value="en">English</option>
                <option value="es">Español</option>
              </select>
              <div className="space-y-2">
                <a
                  href="/login"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block text-center bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded-md text-base font-semibold transition-colors duration-200 shadow-md"
                >
                  {t('navbar.logIn')}
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
